import CalculateMeasurementService from "api/services/CalculateMeasurementService";
import MeasurementModel from "api/models/MeasurementModel";
import MeasurementRowModel from "api/models/MeasurementRowModel";
import MeasurementRowService from "./MeasurementRowService";
import MeasurementService from "./MeasurementService";

const maxAttempts = 5;

class BusyUpdateMeasurementService {
  /** Armazena a instância do Timeout para usá-la posteriormente. */
  private timeoutId: NodeJS.Timeout | undefined;
  /** ID do contrato da medição. */
  private currentContractUid: string = "";
  /** ID da medição. */
  private currentMeasurementUid: string = "";
  /** Fila de atualizações com as linhas alteradas e o novo total da medição. */
  private updateList: {
    currentMeasurement: MeasurementModel;
    rowsToUpdate: MeasurementRowModel[];
    newMeasurementTotal: number;
  }[] = [];
  /** Status do processo de atualização. Pode ter os valores 'idle' que significa que nenhuma
   * requisição foi disparada e 'busy' que significa que o sistema já está cuidando de requisições
   * disparadas.
   */
  private status: "idle" | "busy" = "idle";
  /** Número de tentativas de executar a atualização dos dados. */
  private numberOfAttempts = 0;

  constructor(
    private onChangeStatus: (status: "saving" | "save" | "error") => void
  ) {}

  isSavingChanges() {
    return this.status === "busy";
  }

  /** Inicializa os dados da service para cuidar das atualizações
   * da medição informada.
   * @param {string} contractUid ID do contrato da medição
   * @param {string} uid ID da medição que será atualizada
   */
  initToMeasurement(contractUid: string, uid: string) {
    this.currentContractUid = contractUid;
    this.currentMeasurementUid = uid;
    this.clearUpdateProcess();
  }

  /**
   * Insere as alterações na fila de atualizações e dispara o processo
   * caso esteja ocioso.
   * @param {MeasurementModel} currentMeasurement Medição a ser atualizada
   * @param {MeasurementRowModel} rowsToUpdate Linhas alteradas a serem salvas
   * @param {number} newMeasurementTotal Novo total da medição com as alterações feitas
   */
  callUpdate(
    currentMeasurement: MeasurementModel,
    rowsToUpdate: MeasurementRowModel[],
    newMeasurementTotal: number
  ) {
    // Valida se a medição a ser atualizada é a mesma que a service está tratando
    if (currentMeasurement.uid !== this.currentMeasurementUid) return;

    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      // Insere os dados na fila de atualizações a serem feitas
      this.updateList.push({
        currentMeasurement,
        rowsToUpdate,
        newMeasurementTotal,
      });

      // Dispara o processo se estiver ocioso
      if (this.status === "idle") {
        this.applyUpdate();
      }
    }, 2500);
  }

  /**
   * Dispara a próxima atualização da fila caso tenha, se não
   * finaliza o processo e o marca como ocioso ('idle').
   */
  nextUpdate() {
    // Remove a ultima atualização com sucesso
    this.updateList.shift();

    // Valida se não tem mais atualizações na fila
    if (this.updateList.length === 0) {
      this.onChangeStatus("save");
      this.clearUpdateProcess(false);
      return;
    }

    // Se houver atualizações, chama o próximo.
    setTimeout(() => {
      this.applyUpdate();
    }, 1000);
  }

  /**
   * Tenta executar o processo de atualização novamente até no máximo
   * 5 vezes. Se todas as vezes falharem dispara o callback de erro e
   * requisita a API de recálculo da medição para atualizar os totais com
   * os valores que foram salvos corretamente.
   */
  retryUpdate() {
    this.numberOfAttempts++;

    if (this.numberOfAttempts > maxAttempts) {
      console.error(
        "Error on BusyUpdateMeasurementService: Máximo de tentativas alcançado!"
      );

      CalculateMeasurementService.recalculateAllRows(
        this.currentContractUid,
        this.currentMeasurementUid
      ).finally(() => {
        this.onChangeStatus("error");
        this.clearUpdateProcess(false);
      });
      return;
    }

    setTimeout(() => {
      this.applyUpdate();
    }, 1000);
  }

  /**
   * Limpa os dados do processo de atualização, limpando ou não
   * a fila de atualizações.
   * @param {boolean} clearUpdateList Sinaliza se deve ou não limpar a fila de atualizações
   */
  clearUpdateProcess(clearUpdateList: boolean = true) {
    if (clearUpdateList) this.updateList = [];
    this.status = "idle"; // Libera a fila
    this.numberOfAttempts = 0;
  }

  /** Aplica as atualizações da fila passando os dados necessários. Caso a atualização
   * seja concluída com sucesso chama a próxima atualização, caso retorne erro, chama a função para
   * tentar novamente.
   */
  private applyUpdate() {
    const args = this.getMoreCurrentArguments();
    if (!args) return;

    this.status = "busy";
    this.onChangeStatus("saving");

    this.updateMeasurementRows(
      args.currentMeasurement,
      args.rowsToUpdate,
      args.newMeasurementTotal
    )
      .then((success) => {
        if (success) {
          this.nextUpdate();
          return;
        }

        this.retryUpdate();
      })
      .catch((error: Error) => {
        console.error("Error on BusyUpdateMeasurementService: ", error);
        this.retryUpdate();
      });
  }

  /**
   * Como a cada atualização o sistema passa uma lista com todas
   * as linhas que foram alteradas, mesmo que estão sendo salvas,
   * esta função retorna a atualização mais recente da fila filtrando
   * as linhas novamente para remover aquelas que já foram salvas em
   * outra iteração do processo.
   */
  private getMoreCurrentArguments() {
    if (!this.updateList.length) return;

    // Pega a alteração mais atual feita
    const lastArgs = this.updateList.pop();
    if (!lastArgs) return;

    this.updateList = [lastArgs];

    /** Filtra as linhas novamente para eliminar aquelas já foram salvas no banco. */
    const rowsToUpdate = lastArgs.rowsToUpdate.filter((row) => row.isChanged);

    /** As linhas são mascardas para atualização, para evitar que o processo de atualização no banco
     * altere o status de uma linha que foi recalculada novamente. */
    rowsToUpdate.forEach((row) => row.setRowStatusToSaving());

    lastArgs.rowsToUpdate = rowsToUpdate;
    return lastArgs;
  }

  /**
   * Atualiza as linhas da medição por serviço dividindo as em lotes (batch).
   * @param {MeasurementModel} measurement Medição que possui as linhas
   * @param {MeasurementRowModel[]} rowsData Linhas a serem atualizadas
   * @param {number} newMeasurementTotal Novo total da medição com as atualizações
   * @returns Verdadeiro se todas as linhas foram atualizadas com sucesso
   * e falso se um ou mais lotes de atualizações falhou.
   */
  private async updateMeasurementRows(
    measurement: MeasurementModel,
    rowsData: MeasurementRowModel[],
    newMeasurementTotal: number
  ): Promise<boolean> {
    const chunk = 500;
    const promises: any[] = [];

    for (let i = 0; i < rowsData.length; i += chunk) {
      const batchOfRows = rowsData.slice(i, i + chunk);
      promises.push(
        MeasurementRowService.updateMeasurementRows(measurement, batchOfRows)
          .then(() => true)
          .catch((error) => false)
      );
    }

    const results = await Promise.all(promises);
    const success = results.every((r) => r === true);

    if (success) {
      await MeasurementService.updateMeasurementTotal(
        measurement,
        newMeasurementTotal
      );
    }

    return success;
  }
}

export default BusyUpdateMeasurementService;
